import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    MenuItem,
    Paper,
    Snackbar,
    Slider,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import agentService from '../services/AgentService';
import kbaseService from '../services/KbaseService';
import HelpButton from './HelpButton';
import McpServersEditor from './McpServersEditor';
import {
    inferVendorFromModel,
    getReasoningEffortLevels,
    supportsReasoningEffort,
    LLM_MODELS_BY_VENDOR,
    LLM_VENDORS,
    isKnownLlmVendor,
    normalizeLlmVendor,
} from '../services/LlmCatalogService';

function normalizeString(value) {
    return String(value ?? '').trim();
}

function normalizeNumber(value) {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    return Number.isFinite(n) ? n : '';
}

function normalizeTemperature(value) {
    const n = normalizeNumber(value);
    if (n === '') return '';
    if (n < 0) return 0;
    if (n > 1) return 1;
    return n;
}

function getAgentKbaseId(agent) {
    return normalizeString(
        agent?.kbaseId
        ?? agent?.kbase?.kbaseId
        ?? agent?.kbase?.id
        ?? agent?.knowledgeBaseId
        ?? agent?.knowledge_base_id
    );
}

function formatCreatedAt(createdAt) {
    const n = Number(createdAt);
    if (!Number.isFinite(n) || n <= 0) return '';

    const ms = n < 1_000_000_000_000 ? n * 1000 : n;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString();
}

function AgentDialog({ open, mode, initialAgent, kbases, kbasesLoading, onClose, onSubmit, submitting, inline = false }) {
    const { t } = useTranslation();
    const [agentName, setAgentName] = useState('');
    const [kbaseId, setKbaseId] = useState('');
    const [llmVendor, setLlmVendor] = useState('');
    const [llmModel, setLlmModel] = useState('');
    const [reasoningEffort, setReasoningEffort] = useState('');
    const hasReasoningEffort = supportsReasoningEffort(llmVendor, llmModel);
    const effortLevels = getReasoningEffortLevels(llmVendor, llmModel);
    const selectedEffort = effortLevels.includes(reasoningEffort) ? reasoningEffort : '';
    const [prompt, setPrompt] = useState('');
    const [supportTicketPrompt, setSupportTicketPrompt] = useState('');
    const [temperature, setTemperature] = useState('');
    const [maxTokens, setMaxTokens] = useState('');
    const [mcpServers, setMcpServers] = useState([]);
    const [activeTab, setActiveTab] = useState(0);

    const modelOptions = useMemo(() => {
        const vendor = normalizeString(llmVendor);
        return LLM_MODELS_BY_VENDOR[vendor] || [];
    }, [llmVendor]);

    const initialSignature = useMemo(() => {
        const a = initialAgent || {};
        const id = normalizeString(a?.agentId);
        const rawVendor = a?.llmVendor ?? a?.vendor ?? a?.llm_vendor ?? a?.provider ?? a?.llmProvider;
        const rawModel = a?.llmModel ?? a?.model ?? a?.llm_model;
        return `${id}|${normalizeString(rawVendor)}|${normalizeString(rawModel)}`;
    }, [initialAgent]);

    const initialHasVendorOrModel = useMemo(() => {
        const a = initialAgent || {};
        const rawVendor = a?.llmVendor ?? a?.vendor ?? a?.llm_vendor ?? a?.provider ?? a?.llmProvider;
        const rawModel = a?.llmModel ?? a?.model ?? a?.llm_model;
        return Boolean(normalizeString(rawVendor)) || Boolean(normalizeString(rawModel));
    }, [initialAgent]);

    useEffect(() => {
        if (!open) return;
        const a = initialAgent || {};
        setAgentName(normalizeString(a.agentName));
        setKbaseId(getAgentKbaseId(a));
        const rawVendor = a?.llmVendor ?? a?.vendor ?? a?.llm_vendor ?? a?.provider ?? a?.llmProvider;
        const rawModel = a?.llmModel ?? a?.model ?? a?.llm_model;
        const model = normalizeString(rawModel);
        const vendor = (() => {
            const normalized = normalizeLlmVendor(rawVendor);
            const isKnown = normalized ? isKnownLlmVendor(normalized) : false;
            if (isKnown) return normalized;
            const inferred = model ? inferVendorFromModel(model) : '';
            return inferred || normalized || '';
        })();

        setLlmVendor(normalizeString(vendor));
        setLlmModel(model);
        setReasoningEffort(normalizeString(a?.reasoningEffort));
        setPrompt(normalizeString(a.prompt));
        setSupportTicketPrompt(normalizeString(a.supportTicketPrompt));
        setMcpServers(Array.isArray(a.mcpServers) ? a.mcpServers : []);
        setActiveTab(0);

        const temp = a.temperature;
        setTemperature(temp === 0 ? 0 : normalizeNumber(temp));

        const existingMaxTokens = a.maxTokens ?? a.maxToken;
        const max = existingMaxTokens === 0 ? 0 : normalizeNumber(existingMaxTokens);
        setMaxTokens(mode === 'create' ? (max === '' ? 1000 : max) : max);
    }, [open, initialSignature, mode, initialAgent]);

    const temperatureNumber = normalizeNumber(temperature);
    const temperatureError = !hasReasoningEffort && temperatureNumber !== '' && (temperatureNumber < 0 || temperatureNumber > 1);
    const temperatureSliderValue = temperatureNumber === '' ? 0 : temperatureNumber;

    const maxTokensNumber = normalizeNumber(maxTokens);
    const maxTokensError = maxTokensNumber !== '' && maxTokensNumber <= 0;

    useEffect(() => {
        if (!open) return;
        if (kbasesLoading) return;
        if (mode === 'edit') return;

        // Default KBase for new agents once the options are loaded.
        const current = normalizeString(kbaseId);
        if (current) return;
        const first = (Array.isArray(kbases) ? kbases : [])[0]?.kbaseId;
        if (first) setKbaseId(String(first));
    }, [open, kbases, kbaseId, kbasesLoading, mode]);

    useEffect(() => {
        if (!open) return;

        // When editing, prefer the hydrated backend value; don't override it
        // with defaults during the initial open.
        if (mode === 'edit' && initialHasVendorOrModel) return;

        const vendor = normalizeString(llmVendor);
        const nextVendor = vendor || LLM_VENDORS[0]?.value || '';
        if (nextVendor && nextVendor !== llmVendor) {
            setLlmVendor(nextVendor);
            return;
        }

        // Default model if backend value is null/empty or invalid for vendor.
        const model = normalizeString(llmModel);
        const opts = LLM_MODELS_BY_VENDOR[nextVendor] || [];
        const firstModel = opts[0]?.value || '';
        const modelIsValid = model && opts.some((m) => m.value === model);
        if (!modelIsValid && firstModel && firstModel !== llmModel) {
            setLlmModel(firstModel);
        }
    }, [open, llmVendor, llmModel, mode, initialHasVendorOrModel]);

    const title = mode === 'edit'
        ? `${t('agents.editTitle', 'Edit Agent')}: ${normalizeString(initialAgent?.agentId) || ''}`.trim()
        : t('agents.newTitle', 'New Agent');

    const canSubmit = Boolean(normalizeString(agentName)) && !temperatureError && !maxTokensError;

    const handleSubmit = async () => {
        const promptValue = normalizeString(prompt);
        const supportTicketPromptValue = normalizeString(supportTicketPrompt);
        const payload = {
            agentName: normalizeString(agentName),
            kbaseId: normalizeString(kbaseId),
            llmVendor: normalizeString(llmVendor),
            llmModel: normalizeString(llmModel),
            reasoningEffort: selectedEffort || null,
            prompt: mode === 'edit' ? promptValue : undefined,
            supportTicketPrompt: mode === 'edit' ? supportTicketPromptValue : undefined,
            temperature: (() => {
                const n = normalizeTemperature(temperature);
                return n === '' ? undefined : n;
            })(),
            maxTokens: (() => {
                const n = normalizeNumber(maxTokens);
                return n === '' ? undefined : Math.trunc(n);
            })(),
            mcpServers,
        };

        if (mode !== 'edit' && promptValue) payload.prompt = promptValue;
        if (mode !== 'edit' && supportTicketPromptValue) payload.supportTicketPrompt = supportTicketPromptValue;

        // Remove empty strings for optional fields.
        Object.keys(payload).forEach((k) => {
            if (mode === 'edit' && (k === 'prompt' || k === 'supportTicketPrompt')) return;
            if (payload[k] === '') delete payload[k];
        });

        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

        await onSubmit(payload);
    };

    const handleDiscoverMcpTools = async (serverId, nextServers) => {
        const agentId = normalizeString(initialAgent?.agentId);
        if (!agentId) return [];
        await agentService.updateAgent(agentId, { mcpServers: nextServers });
        return agentService.discoverMcpTools(agentId, serverId);
    };

    const handleDeleteMcpServer = async (serverId) => {
        const agentId = normalizeString(initialAgent?.agentId);
        if (agentId) await agentService.deleteMcpServer(agentId, serverId);
    };

    const formFields = (
        <>
            <Tabs
                value={activeTab}
                onChange={(event, value) => setActiveTab(value)}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label={t('agents.settingsTab', 'Agent Settings')} />
                <Tab label={t('mcp.title', 'MCP Servers')} />
            </Tabs>
            <Box sx={{ display: activeTab === 0 ? 'contents' : 'none' }}>
            {!inline && (
                <TextField
                    label={t('agents.agentName', 'Agent Name')}
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                    required
                    fullWidth
                    disabled={submitting}
                />
            )}
            {!inline && (
                <TextField
                    select
                    label={t('agents.kbase', 'KBase')}
                    value={kbaseId}
                    onChange={(e) => setKbaseId(e.target.value)}
                    fullWidth
                    disabled={submitting}
                    helperText={kbasesLoading ? t('agents.loadingKbases', 'Loading kbases…') : kbases?.length ? '' : t('agents.noKbases', 'No kbases found')}
                >
                    {(() => {
                        const current = normalizeString(kbaseId);
                        const exists = (Array.isArray(kbases) ? kbases : []).some((k) => normalizeString(k?.kbaseId) === current);
                        if (current && !exists) {
                            return <MenuItem value={current}>{`${t('agents.current', 'Current')}: ${current}`}</MenuItem>;
                        }
                        return null;
                    })()}
                    {(Array.isArray(kbases) ? kbases : []).map((k) => (
                        <MenuItem key={String(k?.kbaseId)} value={String(k?.kbaseId || '')}>
                            {k?.kbaseName ? String(k.kbaseName) : String(k?.kbaseId || '')}
                        </MenuItem>
                    ))}
                </TextField>
            )}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, 1.6fr) minmax(0, 1fr)' },
                gap: 2,
                alignItems: 'start',
            }}>
                <TextField
                    select
                    label={t('agents.vendor', 'LLM Vendor')}
                    value={llmVendor}
                    onChange={(e) => {
                        const nextVendor = e.target.value;
                        setLlmVendor(nextVendor);
                        const opts = LLM_MODELS_BY_VENDOR[String(nextVendor)] || [];
                        setLlmModel(opts[0]?.value || '');
                    }}
                    fullWidth
                    disabled={submitting}
                >
                    {LLM_VENDORS.map((v) => (
                        <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label={t('agents.model', 'LLM Model')}
                    value={llmModel}
                    onChange={(e) => setLlmModel(e.target.value)}
                    fullWidth
                    disabled={submitting || !normalizeString(llmVendor)}
                    helperText={!normalizeString(llmVendor) ? t('agents.pickVendor', 'Select a vendor first') : ''}
                >
                    {(() => {
                        const current = normalizeString(llmModel);
                        const exists = modelOptions.some((m) => m.value === current);
                        if (current && !exists) {
                            return (
                                <MenuItem value={current}>
                                    {`${t('agents.current', 'Current')}: ${current}`}
                                </MenuItem>
                            );
                        }
                        return null;
                    })()}
                    {modelOptions.map((m) => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                </TextField>
                {hasReasoningEffort && (
                    <TextField select fullWidth
                        label={t('agents.reasoningEffort', 'Reasoning effort')}
                        value={selectedEffort}
                        onChange={(e) => setReasoningEffort(e.target.value)}
                        disabled={submitting}
                    >
                        <MenuItem value="">{t('agents.modelDefault', 'Model default')}</MenuItem>
                        {effortLevels.map((level) => (
                            <MenuItem key={level} value={level}>{t(`agents.effortLevels.${level}`, level)}</MenuItem>
                        ))}
                    </TextField>
                )}
                {hasReasoningEffort && (
                    <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1', px: 1.75, mt: -1 }}>
                        {t(llmVendor === 'Bedrock' ? 'agents.bedrockClaudeEffortHelp' : 'agents.reasoningEffortHelp')}
                    </Typography>
                )}
            </Box>
            <TextField
                label={t('agents.prompt', 'Prompt')}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                fullWidth
                multiline
                minRows={5}
                disabled={submitting}
            />
            <TextField
                label={t('agents.supportTicketPrompt', 'Support Ticket Prompt')}
                value={supportTicketPrompt}
                onChange={(e) => setSupportTicketPrompt(e.target.value)}
                fullWidth
                multiline
                minRows={4}
                disabled={submitting}
                helperText={t(
                    'agents.supportTicketPromptHelp',
                    'Controls when this agent offers or opens support tickets.'
                )}
            />
            {!hasReasoningEffort && (<Box
                sx={{
                    border: 1,
                    borderColor: temperatureError ? 'error.main' : 'divider',
                    borderRadius: 1,
                    px: 2,
                    py: 0.75,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color={temperatureError ? 'error.main' : 'text.secondary'}>
                        {t('agents.temperature', 'Temperature (0-1)')}
                    </Typography>
                    <Slider
                        size="small"
                        value={temperatureSliderValue}
                        onChange={(event, value) => setTemperature(Array.isArray(value) ? value[0] : value)}
                        min={0}
                        max={1}
                        step={0.1}
                        valueLabelDisplay="auto"
                        disabled={submitting}
                        aria-label={t('agents.temperature', 'Temperature (0-1)')}
                        sx={{ flex: 1 }}
                    />
                    <Typography variant="body2" color="text.primary">
                        {temperatureSliderValue.toFixed(1)}
                    </Typography>
                </Box>
                {temperatureError ? (
                    <Typography variant="caption" color="error">
                        {t('agents.temperatureError', 'Temperature must be between 0 and 1')}
                    </Typography>
                ) : null}
            </Box>)}
            <TextField
                label={t('agents.maxTokens', 'Max Tokens')}
                value={maxTokens}
                onChange={(e) => setMaxTokens(e.target.value)}
                fullWidth
                disabled={submitting}
                type="number"
                inputProps={{ min: 1, step: 1 }}
                error={maxTokensError}
                helperText={maxTokensError ? t('agents.maxTokensError', 'Max tokens must be greater than 0') : (mode === 'create' ? t('agents.maxTokensDefault', 'Default: 1000') : '')}
            />
            </Box>
            {activeTab === 1 ? (
                <McpServersEditor
                    servers={mcpServers}
                    onChange={setMcpServers}
                    onDiscover={handleDiscoverMcpTools}
                    onDelete={handleDeleteMcpServer}
                    canDiscover={mode === 'edit'}
                    disabled={submitting}
                />
            ) : null}
        </>
    );

    const formActions = (
        <>
            {onClose && (
                <Button onClick={onClose} disabled={submitting}>{t('agents.cancel', 'Cancel')}</Button>
            )}
            <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || submitting}>
                {mode === 'edit' ? t('agents.update', 'Update') : t('agents.create', 'Create')}
            </Button>
        </>
    );

    if (inline) {
        return (
            <Box sx={{ width: '100%', maxWidth: 880 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {formFields}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    {formActions}
                </Box>
            </Box>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 3, overflowY: 'visible' }}>
                {formFields}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {formActions}
            </DialogActions>
        </Dialog>
    );
}

function ConfirmDeleteDialog({ open, agent, onClose, onConfirm, submitting }) {
    const { t } = useTranslation();
    const name = normalizeString(agent?.agentName) || normalizeString(agent?.agentId) || t('agents.agent', 'Agent');

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>{t('agents.deleteTitle', 'Delete Agent')}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    {t('agents.deleteMessagePrefix', 'Are you sure you want to delete')} <strong>{name}</strong>?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>{t('agents.cancel', 'Cancel')}</Button>
                <Button color="error" variant="contained" onClick={onConfirm} disabled={submitting || !agent?.agentId}>
                    {t('agents.delete', 'Delete')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function Agents({ embedded = false, agentId = '', autoOpenSingle = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [agents, setAgents] = useState([]);
    const [kbases, setKbases] = useState([]);
    const [kbasesLoading, setKbasesLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [editingAgent, setEditingAgent] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingAgent, setDeletingAgent] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));
    const attachedAgentId = normalizeString(agentId);

    const loadAgents = useCallback(async () => {
        setLoading(true);
        try {
            if (attachedAgentId) {
                const data = await agentService.getAgent(attachedAgentId);
                setAgents(data ? [data] : []);
            } else if (embedded) {
                setAgents([]);
            } else {
                const data = await agentService.getAgents();
                setAgents(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            console.error('Failed to load agents', e);
            setSnackbar({ open: true, message: e?.message || t('agents.loadError', 'Failed to load agents'), severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [attachedAgentId, embedded, t]);

    const loadKbases = useCallback(async () => {
        setKbasesLoading(true);
        try {
            const data = await kbaseService.getCustomerKbases();
            const list = Array.isArray(data) ? data : [];
            list.sort((a, b) => normalizeString(a?.kbaseName).localeCompare(normalizeString(b?.kbaseName)));
            setKbases(list);
        } catch (e) {
            console.error('Failed to load kbases', e);
            setKbases([]);
            setSnackbar({ open: true, message: e?.message || t('agents.loadKbasesError', 'Failed to load kbases'), severity: 'error' });
        } finally {
            setKbasesLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadAgents();
        loadKbases();
    }, [loadAgents, loadKbases]);

    const kbaseNameById = useMemo(() => {
        const map = new Map();
        (Array.isArray(kbases) ? kbases : []).forEach((k) => {
            const id = normalizeString(k?.kbaseId);
            if (!id) return;
            map.set(id, normalizeString(k?.kbaseName));
        });
        return map;
    }, [kbases]);

    const sortedAgents = useMemo(() => {
        const list = Array.isArray(agents) ? [...agents] : [];
        list.sort((a, b) => {
            const an = normalizeString(a?.agentName);
            const bn = normalizeString(b?.agentName);
            if (an && bn) return an.localeCompare(bn);
            return normalizeString(a?.agentId).localeCompare(normalizeString(b?.agentId));
        });
        return list;
    }, [agents]);

    useEffect(() => {
        if (!autoOpenSingle || embedded || attachedAgentId || loading || sortedAgents.length !== 1) return;

        const id = normalizeString(sortedAgents[0]?.agentId);
        if (!id) return;
        navigate(`/agents/${encodeURIComponent(id)}`, { replace: true });
    }, [attachedAgentId, autoOpenSingle, embedded, loading, navigate, sortedAgents]);

    const openCreate = () => {
        setDialogMode('create');
        setEditingAgent(null);
        setDialogOpen(true);
    };

    const openEdit = (agent) => {
        const id = agent?.agentId;
        if (!id) return;

        setSaving(true);
        (async () => {
            try {
                const full = await agentService.getAgent(id);
                setDialogMode('edit');
                setEditingAgent(full || agent || null);
                setDialogOpen(true);
            } catch (e) {
                console.error('Failed to load agent', e);
                setSnackbar({ open: true, message: e?.message || t('agents.loadDetailError', 'Failed to load agent details'), severity: 'error' });
                setDialogMode('edit');
                setEditingAgent(agent || null);
                setDialogOpen(true);
            } finally {
                setSaving(false);
            }
        })();
    };

    const closeDialog = () => {
        if (saving) return;
        setDialogOpen(false);
    };

    const handleSubmitDialog = async (payload) => {
        setSaving(true);
        try {
            if (dialogMode === 'edit') {
                const id = editingAgent?.agentId;
                if (!id) throw new Error('agentId missing');
                await agentService.updateAgent(id, payload);
                setSnackbar({ open: true, message: t('agents.updated', 'Agent updated'), severity: 'success' });
            } else {
                await agentService.createAgent(payload);
                setSnackbar({ open: true, message: t('agents.created', 'Agent created'), severity: 'success' });
            }

            setDialogOpen(false);
            await loadAgents();
        } catch (e) {
            console.error('Failed to save agent', e);
            setSnackbar({ open: true, message: e?.message || t('agents.saveError', 'Failed to save agent'), severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const openDelete = (agent) => {
        setDeletingAgent(agent || null);
        setDeleteDialogOpen(true);
    };

    const closeDelete = () => {
        if (saving) return;
        setDeleteDialogOpen(false);
        setDeletingAgent(null);
    };

    const confirmDelete = async () => {
        const id = deletingAgent?.agentId;
        if (!id) return;

        setSaving(true);
        try {
            await agentService.deleteAgent(id);
            setSnackbar({ open: true, message: t('agents.deleted', 'Agent deleted'), severity: 'success' });
            setDeleteDialogOpen(false);
            setDeletingAgent(null);
            await loadAgents();
        } catch (e) {
            console.error('Failed to delete agent', e);
            setSnackbar({ open: true, message: e?.message || t('agents.deleteError', 'Failed to delete agent'), severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleSubmitAttachedAgent = async (payload) => {
        if (!attachedAgentId) return;

        setSaving(true);
        try {
            await agentService.updateAgent(attachedAgentId, payload);
            setSnackbar({ open: true, message: t('agents.updated', 'Agent updated'), severity: 'success' });
            await loadAgents();
        } catch (e) {
            console.error('Failed to save agent', e);
            setSnackbar({ open: true, message: e?.message || t('agents.saveError', 'Failed to save agent'), severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (embedded && attachedAgentId) {
        const attachedAgent = agents[0] || null;
        // The agent settings form renders both as a standalone page (/agents/:id)
        // and inside the Documents → Agent tab. Only show the page-level "Agents"
        // header (with help) on the standalone route.
        const isStandaloneAgentPage = pathname.startsWith('/agents');

        return (
            <Box sx={{ width: '100%', maxWidth: 880, pt: isStandaloneAgentPage ? 1 : 0 }}>
                {isStandaloneAgentPage && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                        <Typography variant="h5" fontWeight="bold">{t('sideBar.agents', 'Agents')}</Typography>
                        <HelpButton anchor="agents.overview" label={t('sideBar.agents', 'Agents')} />
                    </Box>
                )}
                {loading && (
                    <Typography variant="body2" color="text.secondary">
                        {t('agents.loadingAgent', 'Loading agent…')}
                    </Typography>
                )}

                {!loading && attachedAgent && (
                    <Paper sx={{ p: 2 }} elevation={1}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                            {t('agents.settingsTitle', 'Agent Settings')}
                        </Typography>
                        <AgentDialog
                            open
                            inline
                            mode="edit"
                            initialAgent={attachedAgent}
                            kbases={kbases}
                            kbasesLoading={kbasesLoading}
                            onClose={loadAgents}
                            onSubmit={handleSubmitAttachedAgent}
                            submitting={saving}
                        />
                    </Paper>
                )}

                {!loading && !attachedAgent && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {t('agents.attachedNotFound', 'Attached agent was not found.')}
                    </Alert>
                )}

                <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={closeSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={closeSnackbar} severity={snackbar.severity}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        );
    }

    if (embedded && !attachedAgentId) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                {t('agents.noAttachedAgent', 'No agent is attached to this chatbot.')}
            </Alert>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: embedded ? 'flex-end' : 'space-between', alignItems: embedded ? 'flex-end' : 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                {!embedded && (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="h5" fontWeight="bold">{t('sideBar.agents', 'Agents')}</Typography>
                            <HelpButton />
                        </Box>
                        <Typography variant="body2" color="text.secondary">{t('agents.subtitle', 'Manage your agents')}</Typography>
                    </Box>
                )}

                {!attachedAgentId && (
                    <Button variant="contained" onClick={openCreate} disabled={loading || saving} sx={{ textTransform: 'none', borderRadius: 3 }}>
                        {t('agents.new', 'New Agent')}
                    </Button>
                )}
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('agents.name', 'Name')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('agents.kbase', 'KBase')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('agents.vendorShort', 'Vendor')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('agents.modelShort', 'Model')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('agents.createdAt', 'Created')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200', width: 190 }}>{t('agents.actions', 'Actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedAgents.map((a) => (
                            <TableRow key={String(a?.agentId || a?.agentName || Math.random())} hover>
                                <TableCell>{a?.agentName || '-'}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                    {(() => {
                                        const id = normalizeString(a?.kbaseId);
                                        const name = id ? kbaseNameById.get(id) : '';
                                        if (!id) return '-';
                                        return (
                                            <Link
                                                component={RouterLink}
                                                to={`/kbases/${encodeURIComponent(id)}/documents`}
                                                underline="hover"
                                                sx={{ fontFamily: 'inherit', fontSize: 'inherit' }}
                                            >
                                                {name || id}
                                            </Link>
                                        );
                                    })()}
                                </TableCell>
                                <TableCell>{a?.llmVendor || '-'}</TableCell>
                                <TableCell>{a?.llmModel || '-'}</TableCell>
                                <TableCell>{formatCreatedAt(a?.createdAt) || '-'}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button size="small" variant="outlined" onClick={() => openEdit(a)} disabled={saving} sx={{ textTransform: 'none' }}>
                                            {t('agents.edit', 'Edit')}
                                        </Button>
                                        {!attachedAgentId && (
                                            <Button size="small" color="error" variant="outlined" onClick={() => openDelete(a)} disabled={saving} sx={{ textTransform: 'none' }}>
                                                {t('agents.delete', 'Delete')}
                                            </Button>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}

                        {sortedAgents.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    {attachedAgentId ? t('agents.attachedNotFound', 'Attached agent not found.') : t('agents.empty', 'No agents found.')}
                                </TableCell>
                            </TableRow>
                        )}

                        {loading && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">{t('agents.loading', 'Loading…')}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <AgentDialog
                open={dialogOpen}
                mode={dialogMode}
                initialAgent={editingAgent}
                kbases={kbases}
                kbasesLoading={kbasesLoading}
                onClose={closeDialog}
                onSubmit={handleSubmitDialog}
                submitting={saving}
            />

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                agent={deletingAgent}
                onClose={closeDelete}
                onConfirm={confirmDelete}
                submitting={saving}
            />

            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={closeSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
